// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { deviceIdentifier } from '../../utils/device-identifier.js';
import { event, ShuiLitElement } from '../../utils/event-decorator.js';
import { OnSelectEvent } from '../../utils/events.js';
import { keyboardInteraction } from '../../utils/keyboardInteraction.js';
import { KEYCODE } from '../../utils/keycode.js';
import navItemStyles from './sh-nav-item.lit.scss.js';
/**
 * @slot right - Container for supplementary content (badges, indicators). Should maintain vertical alignment with label.
 * @fires on-select {OnSelectEvent<NavItemSelectDetail>} -  Emitted when the nav item is selected.
 */
let SHNavItem = class SHNavItem extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /** The active attribute is a read-only reflected state on sh-nav-item.It is controlled by the active-index prop on the parent sh-nav-group.Directly setting active on individual items is not recommended and may be ignored. */
    this.active = false;
    /** Prevents interaction and applies disabled styling. Active state indicator would be hidden */
    this.disabled = false;
    /** *hide Unique identifier for the item */
    this.navId = '';
    /**Role for the navigation items */
    this.role = 'link';
    /** Determine whether the right slot contains content. */
    this.hasRightSlotContent = true;
    /** Stores the title for the navigation item. */
    this.effectiveTitle = '';
    this.onClickHandler = (e) => this.handleClick(e);
    this.onKeydownHandler = (e) => this.handleKeydown(e);
  }
  static get styles() {
    return [navItemStyles];
  }
  render() {
    return this.href
      ? html`
          <a class="nav-item" href=${this.href} aria-hidden="true" tabindex="-1">
            ${this.getNavItemTemplate()}
          </a>
        `
      : html` <div class="nav-item">${this.getNavItemTemplate()}</div> `;
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.onClickHandler);
    this.addEventListener('auxclick', this.onClickHandler);
    this.addEventListener('keydown', this.onKeydownHandler);
    this.updateAriaAttributes('initial');
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has('disabled')) {
      this.handleDisabledChange();
      this.updateAriaAttributes('disabled');
    }
    if (changedProperties.has('active')) {
      this.updateAriaAttributes('active');
    }
    if (changedProperties.has('label')) {
      this.computeEffectiveTitle();
      if (this.ariaLabel === null || changedProperties.get('label') === this.ariaLabel) {
        this.ariaLabel = this.label;
      }
    }
  }
  firstUpdated(changedProperties) {
    super.firstUpdated(changedProperties);
    if (deviceIdentifier.isTouchDevice()) {
      this.classList.add('touch-device');
    }
    if (this.labelSpan) {
      this.labelResizeObserver = new ResizeObserver(() => {
        this.computeEffectiveTitle();
      });
      this.labelResizeObserver.observe(this.labelSpan);
      this.computeEffectiveTitle();
    }
    requestAnimationFrame(() => {
      this.handleSlotChange();
    });
  }
  disconnectedCallback() {
    var _a;
    super.disconnectedCallback();
    this.removeEventListener('click', this.onClickHandler);
    this.removeEventListener('auxclick', this.onClickHandler);
    this.removeEventListener('keydown', this.onKeydownHandler);
    (_a = this.labelResizeObserver) === null || _a === void 0 ? void 0 : _a.disconnect();
  }
  updateAriaAttributes(prop) {
    this.setAttribute('aria-label', this.label);
    if (prop === 'initial' || prop === 'disabled') {
      this.disabled
        ? this.setAttribute('aria-disabled', 'true')
        : this.removeAttribute('aria-disabled');
      this.setAttribute('tabindex', this.disabled ? '-1' : '0');
    }
    if (prop === 'initial' || prop === 'active') {
      this.active
        ? this.setAttribute('aria-current', 'page')
        : this.removeAttribute('aria-current');
    }
  }
  /**
   * Returns the nav item content (label and right slot) for use in both <a> and <div>.
   */
  getNavItemTemplate() {
    const label = this.label ? html`<label class="nav-label">${this.label}</label>` : '';
    const rightSlot = this.hasRightSlotContent
      ? html`<slot name="right" class="right" @slotchange=${this.handleSlotChange}></slot>`
      : '';
    return html`${label}${rightSlot}`;
  }
  computeEffectiveTitle() {
    if (!this.labelSpan) {
      this.effectiveTitle = '';
      return;
    }
    const isTruncated = this.labelSpan.scrollWidth > this.labelSpan.clientWidth;
    this.effectiveTitle = isTruncated ? this.label : '';
    if (this.effectiveTitle) {
      this.setAttribute('title', this.effectiveTitle);
    } else {
      this.removeAttribute('title');
    }
  }
  handleDisabledChange(elements) {
    var _a;
    const slottedElements =
      elements ||
      ((_a = this.rightSlot) === null || _a === void 0
        ? void 0
        : _a.assignedElements({ flatten: true })) ||
      [];
    slottedElements.forEach((el) => {
      if ('disabled' in el) {
        el.disabled = this.disabled;
      } else {
        this.disabled ? el.setAttribute('disabled', '') : el.removeAttribute('disabled');
      }
    });
  }
  handleSlotChange() {
    var _a;
    const slottedElements =
      ((_a = this.rightSlot) === null || _a === void 0
        ? void 0
        : _a.assignedElements({ flatten: true })) || [];
    this.hasRightSlotContent = slottedElements.length > 0;
    this.handleDisabledChange(slottedElements);
  }
  handleClick(e) {
    var _a;
    if (this.disabled) {
      e.preventDefault();
      return;
    }
    if (e instanceof MouseEvent && (e.button !== 0 || e.ctrlKey || e.metaKey)) {
      (_a = this.anchorEl) === null || _a === void 0 ? void 0 : _a.blur();
      return;
    }
    this.onSelectEvent.emit(
      new OnSelectEvent({
        detail: { id: this.navId },
        bubbles: true,
        composed: true,
      })
    );
  }
  handleKeydown(e) {
    if (this.disabled) return;
    if (keyboardInteraction(e, KEYCODE.ENTER) || keyboardInteraction(e, KEYCODE.SPACE)) {
      e.preventDefault();
      this.handleClick(e);
      if (this.href && this.anchorEl) {
        this.anchorEl.click();
      }
      return;
    }
  }
};
__decorate([event()], SHNavItem.prototype, 'onSelectEvent', void 0);
__decorate([property({ type: String, reflect: true })], SHNavItem.prototype, 'label', void 0);
__decorate([property({ type: String, reflect: true })], SHNavItem.prototype, 'href', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHNavItem.prototype, 'active', void 0);
__decorate([property({ type: Boolean, reflect: true })], SHNavItem.prototype, 'disabled', void 0);
__decorate([property({ type: String, reflect: false })], SHNavItem.prototype, 'navId', void 0);
__decorate([property({ type: String, reflect: true })], SHNavItem.prototype, 'role', void 0);
__decorate([state()], SHNavItem.prototype, 'hasRightSlotContent', void 0);
__decorate([state()], SHNavItem.prototype, 'effectiveTitle', void 0);
__decorate([query('.nav-label')], SHNavItem.prototype, 'labelSpan', void 0);
__decorate([query('slot[name="right"]')], SHNavItem.prototype, 'rightSlot', void 0);
__decorate([query('a.nav-item')], SHNavItem.prototype, 'anchorEl', void 0);
SHNavItem = __decorate([customElement('sh-nav-item')], SHNavItem);
export { SHNavItem };
